import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import MediaQuery from 'react-responsive';

import {
  filterAirlines,
  filterFlights,
  flightSearchDateAfterXDays,
  flightSearchDateOptions,
  formatDate,
  isDateApiDefaultRange,
} from '@helpers';
import { flightShape } from '@types';
import AutoComplete from 'core/components/AutoComplete';
import InclinedDivider from 'core/components/InclinedDivider';
import { DuetDatePicker } from 'core/components/Picker';
import ProgressBar from 'core/components/ProgressBar';
import { C } from 'core/constants';
import { flightsAndAirlines, generic } from 'core/i18n';
import mapDispatchToProps from 'core/redux/mapDispatchToProps';
import { getFlightsList } from 'core/redux/selectors';

const strings = { ...flightsAndAirlines, ...generic };

const FlightsAndAirlinesSearch = ({
  searchFlightsAndAirlines,
  registerAirportFlightsUpdater,
  activeTab,
  flightList,
  airport,
  airlinesList,
  date,
  keywords,
}) => {
  const [searchLoading, setSearchLoading] = useState(false);
  const [filteredFlights, setFilteredFlights] = useState([]);
  const [filteredAirlines, setFilteredAirlines] = useState([]);
  const [searchDate, setSearchDate] = useState(
    formatDate(flightSearchDateOptions[0])
  );
  const formatMaxDateOptions =
    airport === 'hel'
      ? flightSearchDateAfterXDays(C.FLIGHT_SEARCH_LIMITS.HEL_MAX)
      : flightSearchDateAfterXDays(C.FLIGHT_SEARCH_LIMITS.OTHERS_MAX);
  const maxDate = formatDate(formatMaxDateOptions);

  const formatMinDateOptions =
    airport === 'hel'
      ? flightSearchDateAfterXDays(C.FLIGHT_SEARCH_LIMITS.HEL_MIN)
      : flightSearchDateAfterXDays(C.FLIGHT_SEARCH_LIMITS.OTHERS_MIN);
  const minDate = formatDate(formatMinDateOptions);

  // Fake load state because flights are filtered almost instantaneously
  useEffect(() => {
    setSearchLoading(true);
    setTimeout(() => {
      setSearchLoading(false);
    }, 500);
  }, [date, keywords]);

  const handleChangeFlights = (value) => {
    const uniqueDestinations = [
      ...new Set(
        filterFlights(
          flightList,
          value,
          date,
          activeTab
        ).map(({ destination, origin }) =>
          activeTab === C.FLIGHTS_QUERY_PARAMS.DEP ? destination : origin
        )
      ),
    ];
    setFilteredFlights(uniqueDestinations);
  };

  const handleChangeAirlines = (value) => {
    const airlineNames = filterAirlines(airlinesList, value).map(
      ({ name }) => name
    );
    setFilteredAirlines(airlineNames);
  };

  const handleSelectKeywords = (keywords) => {
    searchFlightsAndAirlines(keywords, date);
  };

  const handleSelectDate = (e) => {
    const newDate = e.detail?.valueAsDate?.toISOString();
    const dateConverted = formatDate(newDate);
    if (airport === 'hel') {
      const isDateEmpty = date === '';
      const isDateConvertedNow = isDateApiDefaultRange(dateConverted);

      if (
        (isDateEmpty && !isDateConvertedNow) ||
        (!isDateEmpty && !isDateConvertedNow && date !== dateConverted) ||
        (isDateConvertedNow && !isDateApiDefaultRange(date))
      ) {
        if (isDateConvertedNow) {
          registerAirportFlightsUpdater(C.FLIGHTS_AIRPORT, 'now');
        } else {
          registerAirportFlightsUpdater(C.FLIGHTS_AIRPORT, dateConverted);
        }
      }
    }
    setSearchDate(dateConverted);
    searchFlightsAndAirlines(keywords, dateConverted);
  };

  return (
    <div className="rw-grid flights-and-airlines-search">
      <div className="rw-row rw-row--no-gutter">
        <div className="rw-col--md-6 rw-col--offset-md-1 rw-col--lg-12 rw-col--offset-lg-0">
          <div className="flights-and-airlines-search__container">
            <DuetDatePicker
              parentClasses="flights-and-airlines-search__item"
              label={strings.searchDateLabel}
              value={searchDate.slice(0, 10)}
              identifier="flights-and-airlines-search-date"
              min={minDate.slice(0, 10)}
              max={maxDate.slice(0, 10)}
              onChange={handleSelectDate}
            />
            <MediaQuery query={C.BREAKPOINT_LARGE}>
              <div className="flights-and-airlines-search__divider">
                <InclinedDivider />
              </div>
            </MediaQuery>
            {activeTab === 'airlines' ? (
              <div className="flights-and-airlines-search__item">
                <AutoComplete
                  data={filteredAirlines}
                  placeholder={strings.placeholderAirlines}
                  handleChange={handleChangeAirlines}
                  handleSelect={handleSelectKeywords}
                  label={strings.searchKeywordsLabelAirlines}
                  hideLabel={false}
                />
              </div>
            ) : (
              <>
                <div className="flights-and-airlines-search__item">
                  <AutoComplete
                    data={filteredFlights}
                    placeholder={
                      activeTab === C.FLIGHTS_QUERY_PARAMS.DEP
                        ? strings.placeholderFlightsDep
                        : strings.placeholderFlightsArr
                    }
                    handleChange={handleChangeFlights}
                    handleSelect={handleSelectKeywords}
                    label={strings.searchKeywordsLabel}
                    hideLabel={false}
                  />
                </div>
              </>
            )}
            {searchLoading && <ProgressBar />}
          </div>
        </div>
      </div>
    </div>
  );
};

FlightsAndAirlinesSearch.propTypes = {
  searchFlightsAndAirlines: PropTypes.func.isRequired,
  clearAirportFlightsUpdater: PropTypes.func.isRequired,
  registerAirportFlightsUpdater: PropTypes.func.isRequired,
  activeTab: PropTypes.string.isRequired,
  date: PropTypes.string,
  keywords: PropTypes.string,
  flightList: PropTypes.arrayOf(PropTypes.shape(flightShape)),
  airport: PropTypes.string,
  airlinesList: PropTypes.array,
};

function mapStateToProps(state) {
  return {
    keywords: state.flightAndAirlineSearch.keywords,
    activeTab: state.uiState.flightsAndAirlinesTab,
    date: state.flightAndAirlineSearch.date,
    flightList: getFlightsList(state),
    airport: C.FLIGHTS_AIRPORT,
    airlinesList: state.airlineList.airlines,
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(FlightsAndAirlinesSearch);
